import { fetchPutResponse } from "../../utilities/ApiUtilities";
import { formatDate } from "../../utilities/CommonUtilities";
import LoadingComponent from "../Common/LoadingComponent";

export const fetchDashboardData = async ({
  fetchDashboardDataRequest,
  setFilters,
  setSelectedFilters,
  selectedFilters,
  setDashboardData,
  setIsLoading,
}) => {
  try {
    setIsLoading(true);
    const response = await fetchPutResponse(
      "dashboard/fetch_dashboard_data",
      fetchDashboardDataRequest
    );
    if (!response.ok) throw new Error("Network response was not ok.");
    const { data: apiData } = await response.json();
    if (Object.keys(apiData).length !== 0) {
      setFilterData({
        apiData,
        setFilters,
        setSelectedFilters,
        selectedFilters,
      });
      setDashboardData(apiData);
    } else {
      setDashboardData(null);
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    setIsLoading(false);
  }
};

const setFilterData = ({
  apiData,
  setFilters,
  setSelectedFilters,
  selectedFilters,
}) => {
  const filterData = {};
  Object.values(apiData).forEach((entry) => {
    const companyName = entry.company_name;
    const countryName = entry.country_name;
    const isin = entry.isin;
    const scores = Object.keys(entry.scores);

    scores.forEach((score) => {
      if (!filterData[score]) {
        filterData[score] = {};
      }
      if (!filterData[score][countryName]) {
        filterData[score][countryName] = {};
      }
      if (!filterData[score][countryName][companyName]) {
        filterData[score][countryName][companyName] = {};
      }
      filterData[score][countryName][companyName] = isin;
    });
  });
  if (selectedFilters["filter1"] === null) {
    const firstScore = Object.keys(filterData)[0];
    const firstCountry = Object.keys(filterData[firstScore]).sort()[0];
    const firstCompany = Object.keys(filterData[firstScore][firstCountry])[0];

    setSelectedFilters({
      filter1: {
        label: firstScore
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        value: firstScore,
      },
      filter2: [
        {
          label: firstCountry
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          value: firstCountry,
        },
      ],
      filter3: [
        {
          label: firstCompany
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          value: firstCompany,
        },
      ],
    });
  }
  setFilters(filterData);
};

export const updateChartData = ({
  dashboardData,
  selectedFilters,
  setDashboardChartData,
  filterDate,
}) => {
  try {
    // setIsLoading(true);
    const today = formatDate(new Date()); // Get today's date in 'YYYY-MM-DD' format
    const endDate = formatDate(filterDate.endDate); // Get endDate in 'YYYY-MM-DD' format
    let seriesData = Object.values(dashboardData) // Use Object.values for direct access to values
      .map((entry) => ({
        name: entry.company_name,
        country: entry.country_name,
        isin: entry.isin,
        data: entry.scores[selectedFilters["filter1"].value].map(
          (score, index) => [
            new Date(entry.dates[index] + "T00:00:00"),
            score === "None" ? null : Number(score),
            entry.isin,
          ]
        ),
      }))
      .filter(
        ({ name, country }) =>
          (selectedFilters["filter2"].map((x) => x.value).length === 0 ||
            selectedFilters["filter2"].map((x) => x.value).includes(country)) &&
          (selectedFilters["filter3"].map((x) => x.value).length === 0 ||
            selectedFilters["filter3"].map((x) => x.value).includes(name))
      );
    if (endDate === today) {
      const allZero = seriesData.every(
        ({ data }) => data[data.length - 1][1] === 0
      );
      if (allZero) {
        seriesData = seriesData.map((entry) => ({
          ...entry,
          data: entry.data.slice(0, -1),
        }));
      }
    }
    setDashboardChartData(seriesData);
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    // setIsLoading(false);
  }
};

export const fetchDashboardDataHome = async ({ fetchDashboardDataRequest }) => {
  try {
    const response = await fetchPutResponse(
      "dashboard/fetch_dashboard_data",
      fetchDashboardDataRequest
    );
    if (!response.ok) throw new Error("Network response was not ok.");
    const { data: apiData } = await response.json();
    if (Object.keys(apiData).length !== 0) {
      const { firstScore, firstCountry } = getFirstFilter(apiData);
      return Object.values(apiData) // Use Object.values for direct access to values
        .map((entry) => ({
          name: entry.company_name,
          country: entry.country_name,
          isin: entry.isin,
          data: entry.scores[firstScore].map((score, index) => [
            new Date(entry.dates[index]).getTime(),
            score === "None" ? null : Number(score),
            entry.isin,
          ]),
        }))
        .filter(({ name, country }) => [firstCountry].includes(country));
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

export const getFirstFilter = (apiData) => {
  const filterData = {};
  Object.values(apiData).forEach((entry) => {
    const companyName = entry.company_name;
    const countryName = entry.country_name;
    const isin = entry.isin;
    const scores = Object.keys(entry.scores);
    scores.forEach((score) => {
      if (!filterData[score]) {
        filterData[score] = {};
      }
      if (!filterData[score][countryName]) {
        filterData[score][countryName] = {};
      }
      if (!filterData[score][countryName][companyName]) {
        filterData[score][countryName][companyName] = {};
      }
      filterData[score][countryName][companyName] = isin;
    });
  });
  const firstScore = Object.keys(filterData)[0];
  const firstCountry = Object.keys(filterData[firstScore]).sort()[0];
  return { firstScore, firstCountry };
};

export const getDefaultDates = (subTabs, dashboard_name) => {
  const today = new Date();
  const todayDate = new Date(today.setHours(0, 0, 0, 0));

  const defaultEndDate =
    subTabs
      .filter((x) => x.dashboardName === dashboard_name)
      .map((x) =>
        x.default_end_date ? new Date(x.default_end_date) : new Date(todayDate)
      )[0] || new Date(todayDate);

  const defaultStartDate =
    subTabs
      .filter((x) => x.dashboardName === dashboard_name)
      .map((x) =>
        x.default_start_date
          ? new Date(x.default_start_date)
          : new Date(todayDate.setMonth(todayDate.getMonth() - 2))
      )[0] || new Date(todayDate.setMonth(todayDate.getMonth() - 2));

  return { defaultStartDate, defaultEndDate };
};

export const initializeFilters = (
  setDashboardData,
  setFilters,
  setDashboardChartData,
  setNewsList,
  setDocumentIds,
  SetOffset
) => {
  setDashboardData(null);
  setFilters({});
  setDashboardChartData(null);
  setNewsList([]);
  setDocumentIds([]);
  SetOffset(0);
};

export const renderLoadingOrNoData = (
  isLoading,
  dashboardChartData,
  dashboardName
) => {
  if (isLoading) {
    return <LoadingComponent />;
  }

  if (dashboardChartData === null) {
    return (
      <div className="w-100 h-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <p className="alert alert-warning m-0" role="alert">
            No data available for the selected Date. Please select another date
            range.
          </p>
        </div>
      </div>
    );
  }

  return null;
};
